/**
 * Category-grouped Product Library view.
 *
 * Mirrors the Owens-Corning base→variant nesting (ProductRow +
 * VariantRows) but the expandable group is the *category*: each category
 * is a collapsible header row that lazy-loads its products on expand.
 * Reuses the shared DataTable so the table styling matches the rest of
 * the Library by construction.
 *
 * Generic - any tenant can use this view. It's selected as the default
 * via the ``products_library_default_view`` tenant setting (see
 * library-page.tsx), so tenants with deep, flat catalogues (e.g. a pool
 * distributor with 100+ categories) get a navigable Library while
 * variant-based tenants keep the flat list.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Package } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { CategoryIcon } from "@/admin/components/category-icons";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { EmptyState } from "@/admin/components/shared/empty-state";
import { useProducts } from "@/admin/hooks/use-products";
import type { Product, ProductCategory } from "@/admin/types/products";

// Keyword → Lucide icon key. Best-effort so a category gets a sensible
// icon even when ``category.icon`` hasn't been set. Admins override per
// category via the existing icon picker. Order matters - more specific
// patterns first.
const CATEGORY_ICON_KEYWORDS: Array<[RegExp, string]> = [
  [/heat\s*pump|thermo|temperature/i, "thermometer"],
  [/heat|warm|gas|boiler|burner/i, "flame"],
  [/salt|chlor|cell|electroly|mineral|ioni/i, "zap"],
  [/light|led|lamp|illum/i, "lightbulb"],
  [/plumb|pipe|valve|fitting|union|elbow|manifold|hose|coupl/i, "wrench"],
  [/cover|roller|blanket|safety|fence|barrier/i, "shield"],
  [/control|automat|electr|board|sensor|probe|timer|switch/i, "zap"],
  [/clean|vacuum|brush|skimmer|net|leaf|hoover/i, "droplets"],
  [/chemical|chlorine|acid|alkal|sanitis|sanitiz|clarif|balanc|dose/i, "droplets"],
  [/pump|circulat|filter|cartridge|sand|media|water|spa|pool/i, "droplets"],
  [/part|spare|repair|misc|labou?r|fee|service|disposal|inspection/i, "cog"],
];

/** Resolve the icon key for a category: explicit icon → keyword → package. */
function resolveCategoryIconKey(cat: {
  icon?: string | null;
  name: string;
}): string {
  if (cat.icon) return cat.icon;
  for (const [re, key] of CATEGORY_ICON_KEYWORDS) {
    if (re.test(cat.name)) return key;
  }
  return "package";
}

// Columns shown when a category is expanded. Product rows render
// SKU - Name (carried in cached_short_name), Brand, Status. No
// Template / Type / Variants columns - per the Library brief.
const CATEGORY_VIEW_COLUMNS: DataTableColumn<ProductCategory>[] = [
  { id: "name", header: "Name", cell: () => null },
  { id: "brand", header: "Brand", cell: () => null },
  { id: "status", header: "Status", cell: () => null },
];

const PRODUCTS_PER_PAGE = 25;

// ── Lazy-loaded product rows for one category ──────────────────────────

function CategoryProductRows({
  categoryUuid,
  colSpan,
  filterParams,
}: {
  categoryUuid: string;
  colSpan: number;
  filterParams?: Record<string, string>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(PRODUCTS_PER_PAGE);
  const { data, isLoading } = useProducts({
    category_uuid: categoryUuid,
    ...filterParams,
    page: "1",
    page_size: String(limit),
  });

  const products = (data?.results ?? []).filter(
    (p) => p.product_type !== "VARIANT",
  );
  const total = data?.count ?? 0;

  if (isLoading && products.length === 0) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className="py-2 pl-[52px] pr-4 text-[11px] text-[var(--ink-3)]"
        >
          {t("common.loading", { defaultValue: "Loading…" })}
        </td>
      </tr>
    );
  }

  if (products.length === 0) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className="py-2 pl-[52px] pr-4 text-[11px] text-[var(--ink-3)]"
        >
          {t("products.noProductsInCategory", {
            defaultValue: "No products in this category.",
          })}
        </td>
      </tr>
    );
  }

  return (
    <>
      {products.map((p: Product) => (
        <tr
          key={p.uuid}
          data-testid={`product-library-item-${p.uuid}`}
          className="cursor-pointer border-b-[0.5px] border-[var(--rule)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
          onClick={() => navigate(`/manage/products/${p.uuid}`)}
        >
          <td className="py-2 pl-[52px] pr-4 text-[13px] text-[var(--ink)]">
            {p.cached_short_name || t("products.untitled")}
          </td>
          <td className="px-4 py-2 text-[12px] text-[var(--ink-3)]">
            {p.brand?.name ?? "-"}
          </td>
          <td className="px-4 py-2">
            <StatusBadge status={p.status} />
          </td>
        </tr>
      ))}
      {products.length < total && (
        <tr>
          <td colSpan={colSpan} className="py-2 pl-[52px] pr-4">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLimit((n) => n + PRODUCTS_PER_PAGE);
              }}
              className="cursor-pointer text-[11px] uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
            >
              {t("products.loadMoreInCategory", {
                defaultValue: "Load more",
              })}{" "}
              ({products.length} / {total})
            </button>
          </td>
        </tr>
      )}
    </>
  );
}

// ── One category header row (collapsible) ──────────────────────────────

function CategoryGroupRow({
  category,
  isExpanded,
  onToggle,
  colSpan,
  filterParams,
}: {
  category: ProductCategory;
  isExpanded: boolean;
  onToggle: () => void;
  colSpan: number;
  filterParams?: Record<string, string>;
}) {
  const count = category.product_count ?? 0;
  return (
    <>
      <tr
        data-testid={`product-category-row-${category.uuid}`}
        onClick={onToggle}
        className="cursor-pointer border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] transition-all duration-100 hover:bg-[var(--bg)]"
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-5 w-5 items-center justify-center text-[var(--ink-3)]">
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
            <CategoryIcon
              icon={resolveCategoryIconKey(category)}
              className="h-4 w-4 text-[var(--green)]"
            />
            <span className="text-[13px] font-medium text-[var(--ink)]">
              {category.name}
            </span>
            <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[10px] text-[var(--green)]">
              {count}
            </span>
          </div>
        </td>
        <td className="px-4 py-3" />
        <td className="px-4 py-3" />
      </tr>
      {isExpanded && (
        <CategoryProductRows
          categoryUuid={category.uuid}
          colSpan={colSpan}
          filterParams={filterParams}
        />
      )}
    </>
  );
}

// ── Main view ──────────────────────────────────────────────────────────

export function LibraryCategoriesView({
  categories,
  isLoading,
  filterParams,
}: {
  categories: ProductCategory[];
  isLoading: boolean;
  filterParams?: Record<string, string>;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Flatten one level so nested sub-categories also appear as their own
  // groups (Pool Systems' categories are flat, but this keeps the view
  // correct for tenants that nest).
  const flatCategories: ProductCategory[] = [];
  const walk = (cats: ProductCategory[]) => {
    for (const c of cats) {
      flatCategories.push(c);
      if (c.children?.length) walk(c.children);
    }
  };
  walk(categories);

  // Order by product_count desc so the densest categories sit on top.
  const ordered = [...flatCategories].sort(
    (a, b) => (b.product_count ?? 0) - (a.product_count ?? 0),
  );

  const colSpan = CATEGORY_VIEW_COLUMNS.length;

  function toggle(uuid: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  return (
    <DataTable<ProductCategory>
      tableId="products-by-category"
      columns={CATEGORY_VIEW_COLUMNS}
      data={ordered}
      isLoading={isLoading}
      rowKey={(c) => c.uuid}
      emptyStateNode={
        <EmptyState
          icon={Package}
          title="No categories yet"
          description="Create product categories to organise the library."
        />
      }
      renderRow={(category) => (
        <CategoryGroupRow
          key={category.uuid}
          category={category}
          isExpanded={expanded.has(category.uuid)}
          onToggle={() => toggle(category.uuid)}
          colSpan={colSpan}
          filterParams={filterParams}
        />
      )}
    />
  );
}
